import { Flex, useSteps } from '@chakra-ui/react'
import { selectAuthenticatedUser, useSelector } from '@missionlabs/api'
import { Destination, DestinationTypes } from '@missionlabs/types'
import { FC, useEffect, useMemo } from 'react'
import { useGetDestinationsForUserQuery } from 'shared/store'
import { selectDestinationsByTypes } from 'shared/utils/deviceDestination'

import { DeviceProvisioningFlow } from '../DeviceProvisioningFlow'
import { AddDeviceESimFormProps } from './AddDeviceESimForm'
import { ActivateESimInstructionsStep } from './ESimActivationFlowSteps/ActivateESimInstructionsStep'
import {
    AddESimStep,
    SetupPendingESimStep
} from './ESimActivationFlowSteps/InitialESimActivationSteps'

export type ESimActivationFlowProps = Pick<AddDeviceESimFormProps, 'onSubmit'>

enum ActivationSteps {
    'ADD_ESIM' = 0,
    'SETUP_PENDING' = 1,
    'ACTIVATION_INSTRUCTIONS' = 2,
    'FULLY_ACTIVATED' = 3
}

const POLLING_INTERVAL = 10_000

export const ESimActivationFlow: FC<ESimActivationFlowProps> = ({ onSubmit }) => {
    const user = useSelector(selectAuthenticatedUser)
    const allowedDestinationsTypes = [DestinationTypes.eSim]

    const { destinations = [], isLoading } = useGetDestinationsForUserQuery(user?.userID ?? '', {
        skip: !user,
        selectFromResult: result => ({
            ...result,
            destinations: selectDestinationsByTypes(allowedDestinationsTypes)(result)
        }),
        pollingInterval: POLLING_INTERVAL
    })

    const data: Partial<Destination> = useMemo(() => {
        return destinations[0] || null
    }, [destinations])

    useEffect(() => {
        setActiveStep(getCurrentStep(data))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data])

    const { activeStep, setActiveStep } = useSteps({
        index: getCurrentStep(data),
        count: 3
    })

    const provisioningFlowSteps = data?.provisioningFlow ?? []
    const currentProvisioningFlowStep =
        provisioningFlowSteps.findLastIndex(item => item.success) + 1

    function getCurrentStep(data) {
        const { usageStatus, installStatus } = data?.metadata || {}

        if (!data) {
            return ActivationSteps.ADD_ESIM
        }

        if (installStatus === 'INSTALLED') {
            return ActivationSteps.FULLY_ACTIVATED
        }

        if (usageStatus === 'ALLOCATED') {
            return ActivationSteps.ACTIVATION_INSTRUCTIONS
        }

        return ActivationSteps.SETUP_PENDING
    }

    function renderFlow() {
        switch (activeStep) {
            case ActivationSteps.ADD_ESIM:
                return <AddESimStep onSubmit={onSubmit} />
            case ActivationSteps.SETUP_PENDING:
                return <SetupPendingESimStep />
            case ActivationSteps.ACTIVATION_INSTRUCTIONS:
                return (
                    <ActivateESimInstructionsStep
                        metadata={data.metadata}
                        provisioningFlow={data.provisioningFlow}
                    />
                )
        }
    }

    if (isLoading) return null

    return (
        <Flex gap="10px 16px" wrap="wrap">
            {renderFlow()}
            {activeStep !== ActivationSteps.ADD_ESIM &&
                activeStep !== ActivationSteps.ACTIVATION_INSTRUCTIONS && (
                    <DeviceProvisioningFlow
                        steps={provisioningFlowSteps}
                        stepIndex={currentProvisioningFlowStep}
                    />
                )}
        </Flex>
    )
}
